/**
 * App - Core application orchestrator
 * Initializes MIDI, audio, state, SignalBus, performance/preset switching, Three.js loop.
 */
import { StateManager } from './StateManager.js';
import { MIDIManager } from '../midi/MIDIManager.js';
import { MIDIEventHandler } from '../midi/MIDIEventHandler.js';
import { MIDIClockManager } from '../midi/MIDIClockManager.js';
import { AudioManager } from '../audio/AudioManager.js';
import { UIManager } from '../ui/UIManager.js';
import { ThreeRenderer } from '../graphics/ThreeRenderer.js';
import { SignalBus } from '../signals/SignalBus.js';
import { createDefaultPerformance } from '../performance/schema.js';
import { resolveRuntimeParams, GLOBAL_ENV_BINDING_TARGETS } from '../performance/schema.js';
import { resolveSignals } from '../performance/resolveSignals.js';
import { mergeRigIntoPerformance, applyPerformanceData } from '../performance/mergeState.js';
import { createScene, getSceneMeta } from '../scenes/registry.js';
import { computeBpmBuiltinSignals } from '../timing/bpmBuiltinSignals.js';
import { bindTitleTransportSpinner } from '../ui/titleTransportSpinner.js';

/**
 * @param {EventTarget | null} target
 * @returns {boolean}
 */
function isEditableKeyboardTarget(target) {
  if (!target || !(target instanceof Element)) return false;
  const el = target;
  if (el.closest('button')) return true;
  if (el.closest('a[href]')) return true;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return false;
}

export class App {
  constructor() {
    this.canvas = document.getElementById('main-canvas');
    this.renderer = new ThreeRenderer(this.canvas);

    const defaultPerf = createDefaultPerformance();
    this.state = new StateManager({
      globalBPM: 120,
      audioEnabled: false,
      audioSensitivity: 1.0,
      audioSmoothing: 0.8,
      midiConnected: false,
      audioListening: false,
      midiCCMappings: {},
      midiNoteMappings: {},
      controlValues: {},
      sceneBindings: {},
      activeSceneId: 'marching_cubes',
      performance: defaultPerf,
      activePresetKey: defaultPerf.programMap['0'] || 'main',
      uiMode: 'edit',
    });

    this.signalBus = new SignalBus();
    this._activeBindings = {};

    this.midiManager = new MIDIManager(this.state);
    this.midiEventHandler = new MIDIEventHandler(this.state);
    this.midiClockManager = new MIDIClockManager(this.state);
    this.audioManager = new AudioManager(this.state);

    mergeRigIntoPerformance(this.state);
    this.signalBus.applyRig(this.state.get('performance').rig);

    this.activeScene = null;
    this._applyPreset(this.state.get('activePresetKey'), true);

    this.uiManager = new UIManager({
      state: this.state,
      midiManager: this.midiManager,
      audioManager: this.audioManager,
      midiEventHandler: this.midiEventHandler,
      getSceneMidiTargets: () => {
        let sceneTargets = [];
        try {
          sceneTargets =
            this.activeScene && typeof this.activeScene.getMidiTargets === 'function'
              ? this.activeScene.getMidiTargets()
              : [];
        } catch (e) {
          console.error('getSceneMidiTargets:', e);
        }
        return [...sceneTargets, ...GLOBAL_ENV_BINDING_TARGETS];
      },
      app: this,
    });

    this.midiManager.onMessage = (data) => {
      this.signalBus.onMidiMessage(data);
      this.midiEventHandler.handleMessage(data);
      this.uiManager.onMIDIMessage(data);
    };
    this.signalBus.onProgramChange = (program) => {
      this._onProgramChange(program);
    };

    this.midiManager.onStatusChange = (msg, connected) => {
      console.log(`MIDI: ${msg} (${connected ? 'connected' : 'disconnected'})`);
    };

    this.midiEventHandler.onClock = () => this.midiClockManager.onMIDIClock();
    this.midiEventHandler.onStart = () => this.midiClockManager.onMIDIStart();
    this.midiEventHandler.onStop = () => this.midiClockManager.onMIDIStop();
    this.midiEventHandler.onContinue = () => this.midiClockManager.onMIDIContinue();

    this.state.subscribe('midiCCMappings', () => {
      mergeRigIntoPerformance(this.state);
      this.signalBus.applyRig(this.state.get('performance').rig);
    });
    this.state.subscribe('midiNoteMappings', () => {
      mergeRigIntoPerformance(this.state);
      this.signalBus.applyRig(this.state.get('performance').rig);
    });

    this.lastFrameTime = 0;
    this.frameCount = 0;
    this.fpsUpdateTime = 0;

    /** @type {(() => void) | null} */
    this._unbindTitleSpinner = null;

    /** @type {((e: KeyboardEvent) => void) | null} */
    this._transportKeydownHandler = null;

    this._resize();
    window.addEventListener('resize', () => this._resize());
  }

  /** Space toggles internal transport (ignored when typing in form fields). */
  _onTransportKeydown(e) {
    if (e.code !== 'Space') return;
    if (isEditableKeyboardTarget(e.target)) return;
    e.preventDefault();
    if (this.state.get('transportPlaying')) {
      this.midiClockManager.stopInternalClock();
    } else {
      this.midiClockManager.startInternalClock();
    }
  }

  /**
   * @param {object} perf
   */
  loadPerformance(perf) {
    applyPerformanceData(this.state, perf);
    mergeRigIntoPerformance(this.state);
    this.signalBus.applyRig(this.state.get('performance').rig);
    this._applyPreset(this.state.get('activePresetKey'), true);
  }

  /** @returns {object} */
  exportPerformance() {
    mergeRigIntoPerformance(this.state);
    return JSON.parse(JSON.stringify(this.state.get('performance')));
  }

  /**
   * @param {string} presetKey
   * @param {boolean} [initial]
   */
  _applyPreset(presetKey, initial = false) {
    const perf = this.state.get('performance');
    const preset = perf?.presets?.[presetKey];
    if (!preset) return;

    const meta = getSceneMeta(preset.scene);
    const merged = resolveRuntimeParams(meta, perf, preset);
    this._activeBindings = merged.bindings || {};

    if (this.activeScene) {
      this.activeScene.dispose();
      this.activeScene = null;
    }

    const scene = createScene(preset.scene);
    this.activeScene = scene || createScene('marching_cubes');
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.activeScene.init({
      getSize: () => ({ w, h }),
      getCamera: () => this.renderer.camera,
      getWebGLRenderer: () => this.renderer.renderer,
      canvas: this.canvas,
    });
    if (typeof this.activeScene.applyParams === 'function') {
      this.activeScene.applyParams(merged.params || {});
    }
    this.renderer.syncBloomFromParams(merged.params || {});

    this.state.set('sceneBindings', { ...this._activeBindings });
    this.state.set('activePresetKey', presetKey);
    this.state.set('activeSceneId', preset.scene);

    if (!initial) {
      this.uiManager?.refreshPerformancePanel?.();
      this.uiManager?.refreshSceneBindingsPanel?.();
    }
  }

  /** Switch active preset from MIDI program number (0–127). */
  _onProgramChange(program) {
    const perf = this.state.get('performance');
    const key = perf?.programMap?.[String(program)] ?? perf?.programMap?.[program];
    if (key && perf.presets?.[key]) {
      this._applyPreset(key, false);
    }
  }

  /** @param {string} presetKey */
  switchToPreset(presetKey) {
    const perf = this.state.get('performance');
    if (perf?.presets?.[presetKey]) this._applyPreset(presetKey, false);
  }

  /**
   * Push the active preset’s params into the running scene only (no dispose/recreate).
   * Tune sliders call this every move; `switchToPreset` would rebuild the scene and refresh the panel each time.
   */
  applyActivePresetParams() {
    const key = this.state.get('activePresetKey');
    const perf = this.state.get('performance');
    const preset = perf?.presets?.[key];
    if (!preset || !this.activeScene) return;
    if (this.state.get('activeSceneId') !== preset.scene) return;
    const meta = getSceneMeta(preset.scene);
    if (!meta) return;
    const merged = resolveRuntimeParams(meta, perf, preset);
    if (typeof this.activeScene.applyParams === 'function') {
      this.activeScene.applyParams(merged.params || {});
    }
    this.renderer.syncBloomFromParams(merged.params || {});
  }

  /** Refresh resolved bindings when only `performance.presets[key].bindings` changed in UI. */
  updateActiveBindingsFromState() {
    const perf = this.state.get('performance');
    const key = this.state.get('activePresetKey');
    const preset = perf?.presets?.[key];
    if (!preset) return;
    const meta = getSceneMeta(preset.scene);
    const merged = resolveRuntimeParams(meta, perf, preset);
    this._activeBindings = merged.bindings || {};
    this.state.set('sceneBindings', { ...this._activeBindings });
  }

  async start() {
    await this.midiManager.connect();
    await this.audioManager.initialize();
    this.midiClockManager.initializeFromState();
    this.midiClockManager.startInternalClock();

    const basePageTitle = document.title;
    this._unbindTitleSpinner = bindTitleTransportSpinner(this.state, {
      baseTitle: basePageTitle,
    });

    this._transportKeydownHandler = (e) => this._onTransportKeydown(e);
    window.addEventListener('keydown', this._transportKeydownHandler);

    this.lastFrameTime = performance.now();
    this.fpsUpdateTime = this.lastFrameTime;
    requestAnimationFrame((t) => this._loop(t));

    console.log('Regular Reality started');
  }

  _loop(timestamp) {
    const dt = (timestamp - this.lastFrameTime) / 1000;
    this.lastFrameTime = timestamp;

    this.midiClockManager.updateAnimationTime(dt);

    this.frameCount++;
    if (timestamp - this.fpsUpdateTime >= 1000) {
      const fps = Math.round(this.frameCount / ((timestamp - this.fpsUpdateTime) / 1000));
      const el = document.getElementById('fps-display');
      if (el) el.textContent = `${fps} FPS`;
      this.frameCount = 0;
      this.fpsUpdateTime = timestamp;
    }

    this._render(dt);

    requestAnimationFrame((t) => this._loop(t));
  }

  _render(dt) {
    const audioLevel = this.state.get('audioOverall') || 0;
    // Beat phase (quarter-note count); frozen in beats when transport stops so BPM drift
    // from MIDI analysis cannot flicker bound params.
    const beats = this.midiClockManager.getMusicalBeats();
    // Cosine peaks on downbeats; continuous (no step at bar lines).
    const bpmPulse = 0.5 + 0.5 * Math.cos(2 * Math.PI * beats);
    const bpmBuiltin = computeBpmBuiltinSignals(beats);

    const fd = this.audioManager.frequencyData;
    const sr = this.audioManager.audioContext?.sampleRate;
    this.signalBus.setFrameInputs({
      bpmPulse,
      bpmBuiltin,
      audioLevel,
      frequencyData: fd,
      sampleRate: sr,
      fftSize: this.audioManager.fftSize,
    });
    this.signalBus.update(dt);

    const raw = this.signalBus.signals;
    this.state.set('controlValues', { ...raw });

    const resolved = resolveSignals(raw, this._activeBindings);
    resolved._bpmPulse = bpmPulse;
    resolved._audioLevel = audioLevel;

    if (this.activeScene) {
      this.activeScene.update(dt, resolved);
      this.renderer.applyFrameBloomModulation(resolved.bloom);
      this.renderer.render(this.activeScene, dt);
    }
  }

  _resize() {
    this.renderer.resize();
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.activeScene?.resize?.(w, h);
  }

  destroy() {
    if (this._transportKeydownHandler) {
      window.removeEventListener('keydown', this._transportKeydownHandler);
      this._transportKeydownHandler = null;
    }
    this._unbindTitleSpinner?.();
    this._unbindTitleSpinner = null;
    this.midiManager.destroy();
    this.midiEventHandler.destroy();
    this.audioManager.destroy();
    this.uiManager.destroy();
    this.activeScene?.dispose?.();
    this.renderer.destroy();
  }
}
